import { useMemo, useState } from "react"
import { fmtCompactRelative } from "@/lib/format"
import { cn } from "@/lib/cn"
import type { Topic, TopicAgent } from "@/types/topic"

type Props = {
  agents: TopicAgent[]
  selectedAgentId: number
  onAgentChange: (id: number) => void
  topics: Topic[]
  selectedId: number | null
  onSelect: (id: number) => void
}

const PAGE_SIZE = 12

export function TopicList({ agents, selectedAgentId, onAgentChange, topics, selectedId, onSelect }: Props) {
  const [search, setSearch] = useState("")
  const [untaggedOnly, setUntaggedOnly] = useState(false)
  const [page, setPage] = useState(0)

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase()
    return topics.filter((t) => {
      if (untaggedOnly && t.tags.length > 0) return false
      if (!q) return true
      return (
        t.name.toLowerCase().includes(q) ||
        t.key.toLowerCase().includes(q) ||
        (t.description ?? "").toLowerCase().includes(q) ||
        t.tags.some((tag) => tag.toLowerCase().includes(q))
      )
    })
  }, [topics, search, untaggedOnly])

  const pageStart = page * PAGE_SIZE
  const pageRows = filtered.slice(pageStart, pageStart + PAGE_SIZE)
  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE))
  const showPager = filtered.length > PAGE_SIZE

  return (
    <div className="flex h-full flex-col rounded-card border border-border bg-app-panel">
      <div className="border-b border-border p-4">
        <div className="mb-3 text-sm font-medium text-app-text">Topic Catalog</div>

        <div className="mb-3 flex items-center gap-2">
          <select
            value={selectedAgentId}
            onChange={(e) => onAgentChange(parseInt(e.target.value, 10))}
            className="min-w-0 flex-1 rounded-control border border-border bg-app-bg px-3 py-2 text-sm text-app-text focus:border-brand-red focus:outline-none"
          >
            {agents.map((a) => <option key={a.id} value={a.id}>{a.name}</option>)}
          </select>
        </div>

        <div className="mb-3 flex flex-wrap gap-2">
          <button type="button" onClick={() => { setUntaggedOnly((v) => !v); setPage(0) }}
            className={cn(
              "rounded-control border px-2 py-1 text-xs transition",
              untaggedOnly
                ? "border-brand-red bg-brand-redSoft/30 text-app-text"
                : "border-border bg-app-bg text-app-muted hover:border-brand-red"
            )}>
            🏷 Untagged
          </button>
        </div>

        <input
          type="text"
          value={search}
          onChange={(e) => { setSearch(e.target.value); setPage(0) }}
          placeholder="Search topics or tags…"
          className="w-full rounded-control border border-border bg-app-bg px-3 py-2 text-sm text-app-text placeholder:text-app-muted focus:border-brand-red focus:outline-none"
        />
      </div>

      {pageRows.length === 0 ? (
        <div className="flex-1 p-6 text-sm text-app-muted">
          {topics.length === 0 ? "No topics yet — click + New topic to add one." : "No matches."}
        </div>
      ) : (
        <div className="flex-1 divide-y divide-border overflow-y-auto">
          <div className="grid grid-cols-[1.4fr_1fr_70px_110px] gap-3 px-4 py-2 text-[11px] uppercase tracking-wide text-app-muted">
            <div>Name</div>
            <div>Tags</div>
            <div>Convos&nbsp;30d</div>
            <div className="text-right">Last used</div>
          </div>
          {pageRows.map((t) => (
            <div
              key={t.id}
              role="button"
              tabIndex={0}
              onClick={() => onSelect(t.id)}
              onKeyDown={(ev) => { if (ev.key === "Enter" || ev.key === " ") { ev.preventDefault(); onSelect(t.id) } }}
              className={cn(
                "grid w-full cursor-pointer grid-cols-[1.4fr_1fr_70px_110px] items-center gap-3 px-4 py-3 text-left text-sm transition outline-none hover:bg-white/5 focus-visible:bg-white/5 focus-visible:ring-1 focus-visible:ring-brand-red/40",
                t.id === selectedId && "bg-brand-redSoft/30 ring-1 ring-brand-red/40"
              )}
            >
              <div className="flex min-w-0 flex-col">
                <span className="truncate font-medium text-app-text">{t.name}</span>
                <span className="truncate font-mono text-[11px] text-app-muted">{t.key}</span>
              </div>
              <div className="flex min-w-0 flex-wrap gap-1">
                {t.tags.length === 0 && <span className="text-[10px] text-app-muted">—</span>}
                {t.tags.slice(0, 3).map((tag) => (
                  <span key={tag} className="rounded-control border border-border bg-app-bg px-1.5 py-0.5 text-[10px] text-app-muted">{tag}</span>
                ))}
                {t.tags.length > 3 && (
                  <span className="text-[10px] text-app-muted">+{t.tags.length - 3}</span>
                )}
              </div>
              <div className="text-xs text-app-muted">{t.convoCount30d}</div>
              <div className="text-right text-xs text-app-muted">{fmtCompactRelative(t.lastUsedAt)}</div>
            </div>
          ))}
        </div>
      )}

      {showPager && (
        <div className="flex items-center justify-between border-t border-border px-4 py-2 text-xs text-app-muted">
          <div>
            {`Showing ${pageStart + 1} to ${Math.min(pageStart + PAGE_SIZE, filtered.length)} of ${filtered.length} topics`}
          </div>
          <div className="flex items-center gap-1">
            <button type="button" onClick={() => setPage((p) => Math.max(0, p - 1))} disabled={page === 0}
              className="rounded-control border border-border px-2 py-1 disabled:opacity-40">‹</button>
            <span className="rounded-control border border-brand-red bg-brand-redSoft/30 px-2 py-1 text-app-text">{page + 1}</span>
            <button type="button" onClick={() => setPage((p) => Math.min(totalPages - 1, p + 1))} disabled={page >= totalPages - 1}
              className="rounded-control border border-border px-2 py-1 disabled:opacity-40">›</button>
          </div>
        </div>
      )}
    </div>
  )
}
